import { useCallback, useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api as badgeCenterApi } from "../../../services/apis/badge-center";
import { OnboardingCard, OnboardingCardsResponse, OnboardingStatusResponse } from "./types";

const STATUS_QUERY_KEY = ["badge-center-onboarding-status"] as const;
const CARDS_QUERY_KEY = ["badge-center-onboarding-cards"] as const;

/**
 * Drives the Badge Center onboarding carousel:
 *  - fetches the server-side "acknowledged" flag (one-time display, per account)
 *  - fetches the ordered cards only when the flow still needs to be shown
 *  - exposes an idempotent acknowledge action (used by both the final CTA and
 *    early dismissal via the X button)
 *
 * @param enabled Gate the whole flow (e.g. only once the user is known and the
 *                Badge Center is unlocked) so we never fetch prematurely.
 */
export function useBadgeCenterOnboarding(enabled: boolean) {
    const queryClient = useQueryClient();

    // The carousel's current slide lives here rather than inside the modal so it
    // survives the modal unmounting. When the user dismisses, we optimistically
    // flip the acknowledged flag, which unmounts the modal; if the acknowledge
    // POST then fails and the flag is rolled back, the modal remounts. Keeping
    // `step` in the hook (which stays mounted on the Badge Center page) means the
    // user returns to the slide they were on instead of being reset to the first.
    const [step, setStep] = useState(0);

    const { data: status, isLoading: isStatusLoading } = useQuery<OnboardingStatusResponse>({
        queryKey: STATUS_QUERY_KEY,
        queryFn: () => badgeCenterApi.getOnboardingStatus(),
        enabled,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        // A successful status is stable for the session (it only changes when we
        // acknowledge, which we write into the cache directly), so treat it as
        // fresh for a while to avoid refetching on every Badge Center remount.
        staleTime: 5 * 60 * 1000,
        // On a persistent failure we can't resolve onboarding state and simply
        // skip the carousel; cap retries so a hard outage doesn't keep hammering
        // the endpoint on each visit.
        retry: 1,
        // If the status read fails we can't tell whether the user needs
        // onboarding, so `needsOnboarding` stays false, the cards query stays
        // disabled, and the auto-acknowledge effect never runs. Unlike the
        // cards-error path we must NOT acknowledge on the user's behalf — that
        // POST permanently marks onboarding complete and would suppress it
        // forever for a genuine first-time user hit by a transient error.
        // Instead, don't refetch the errored query on every Badge Center remount
        // (which `retry` alone doesn't prevent); a fresh app load still retries
        // once the endpoint recovers, so the one-time flow can still appear.
        retryOnMount: false,
    });

    const needsOnboarding = enabled && status?.acknowledged === false;

    const {
        data: cardsData,
        isLoading: areCardsLoading,
        isError: isCardsError,
        isFetched: areCardsFetched,
    } = useQuery<OnboardingCardsResponse>({
        queryKey: CARDS_QUERY_KEY,
        queryFn: () => badgeCenterApi.getOnboardingCards(),
        enabled: needsOnboarding,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
    });

    const { mutate: acknowledge } = useMutation({
        mutationFn: () => badgeCenterApi.acknowledgeOnboarding(),
        onMutate: () => {
            // Snapshot the current flag so we can roll back if the request fails.
            const previousStatus =
                queryClient.getQueryData<OnboardingStatusResponse>(STATUS_QUERY_KEY);
            // Optimistically flip the flag so the carousel never re-opens in this
            // session even before the request resolves.
            queryClient.setQueryData<OnboardingStatusResponse>(STATUS_QUERY_KEY, {
                acknowledged: true,
            });
            return { previousStatus };
        },
        onError: (_error, _variables, context) => {
            // Revert the optimistic flip so the carousel can be dismissed again;
            // otherwise it stays hidden for the rest of the session while the
            // server still has acknowledged: false.
            if (context?.previousStatus !== undefined) {
                queryClient.setQueryData<OnboardingStatusResponse>(
                    STATUS_QUERY_KEY,
                    context.previousStatus,
                );
            }
        },
    });

    const cards: OnboardingCard[] = cardsData?.cards ?? [];
    const shouldShow = !!needsOnboarding && cards.length > 0;

    // Marking as acknowledged is idempotent on the server; guard the local call so
    // navigating back and re-closing doesn't queue redundant requests.
    const dismiss = useCallback(() => {
        if (status?.acknowledged) return;
        acknowledge();
    }, [acknowledge, status?.acknowledged]);

    // When onboarding is required but there's nothing renderable — an empty cards
    // payload or a failed request — the carousel can never open, so the user can
    // never dismiss it via the UI. Without this, the flag stays unacknowledged and
    // both queries refetch on every Badge Center visit forever. Acknowledge on the
    // user's behalf so the one-time flow settles.
    //
    // Fire at most once per mount. If the acknowledge POST fails, its onError
    // rolls back the optimistic flag, which flips needsOnboarding back to true;
    // without this guard the effect would re-run and re-dismiss in a loop,
    // hammering the endpoint until a request finally succeeds. A single attempt
    // per mount lets a genuine failure settle and simply retries on the next
    // Badge Center visit (a fresh mount).
    const autoDismissAttempted = useRef(false);
    useEffect(() => {
        if (!needsOnboarding || areCardsLoading || !areCardsFetched) return;
        if (autoDismissAttempted.current) return;
        if (isCardsError || cards.length === 0) {
            autoDismissAttempted.current = true;
            dismiss();
        }
    }, [needsOnboarding, areCardsLoading, areCardsFetched, isCardsError, cards.length, dismiss]);

    return {
        shouldShow,
        cards,
        isLoading: isStatusLoading || (!!needsOnboarding && areCardsLoading),
        dismiss,
        step,
        setStep,
    };
}
